"use client";

import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useTheme } from "@/contexts/ThemeContext";
import {
  ButtonOutline,
  ButtonPrimary,
  ButtonDestructive,
} from "@/components/common/StandardButton";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useFileManager } from "@/hooks/useFileManager";
import { FileManagerConfig, FileSystemItem } from "@/types/fileManager";
import { FileItem } from "./FileItem";
import { UploadProgressItem } from "./UploadProgressItem";
import { Breadcrumb } from "./Breadcrumb";
import { CreateFolderDialog } from "./CreateFolderDialog";
import { ArrowUp, RotateCcw, FolderPlus, Upload, Check } from "lucide-react";

interface FileManagerContentProps {
  config?: Partial<FileManagerConfig>;
  onConfirmSelection?: (files: FileSystemItem[]) => void;
  onCancel?: () => void;
  onSelectionChange?: (selectedItems: FileSystemItem[]) => void;
  mode?: "modal" | "fullscreen";
  className?: string;
}

export const FileManagerContent: React.FC<FileManagerContentProps> = ({
  config = {},
  onConfirmSelection,
  onCancel,
  onSelectionChange,
  mode = "fullscreen",
  className = "",
}) => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);

  // Default config for fullscreen mode
  const defaultConfig: Partial<FileManagerConfig> = {
    selectionMode: "multiple",
    showUpload: true,
    showCreateFolder: true,
    allowedFileTypes: undefined,
    maxFileSize: undefined,
    maxSelections: undefined,
  };

  const mergedConfig = { ...defaultConfig, ...config };

  const {
    currentPath,
    items,
    selectedItems,
    loading,
    error,
    uploadProgress,
    uploadProgressItems,
    browseAssets,
    navigateToPath,
    navigateUp,
    createFolder,
    uploadMultipleFiles,
    deleteAsset,
    selectItem,
    clearSelection,
    isItemSelected,
    cancelUpload,
    setError,
  } = useFileManager();

  // Handle file upload with dropzone
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles) {
        return;
      }

      if (acceptedFiles.length > 0) {
        try {
          await uploadMultipleFiles(acceptedFiles);
        } catch (err) {
          console.error("Error in onDrop:", err);
          // Error is handled in the hook
        }
      }
    },
    [uploadMultipleFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    accept:
      mergedConfig.allowedFileTypes?.reduce(
        (acc, type) => {
          acc[type] = [];
          return acc;
        },
        {} as Record<string, string[]>,
      ) || undefined,
    maxSize: mergedConfig.maxFileSize,
    noClick: true,
  });

  // Filter items based on search query and allowed file types
  const filteredItems = (items || []).filter((item) => {
    // Search filter
    if (
      searchQuery &&
      !item.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    // File type filter
    if (
      mergedConfig.allowedFileTypes &&
      mergedConfig.allowedFileTypes.length > 0 &&
      !item.isFolder
    ) {
      const extension = item.name.split(".").pop()?.toLowerCase();

      // Convert MIME types to extensions for comparison
      const allowedExtensions = mergedConfig.allowedFileTypes
        .map((mimeType) => {
          // Convert MIME types to extensions
          switch (mimeType.toLowerCase()) {
            case "image/jpeg":
            case "image/jpg":
              return ["jpg", "jpeg"];
            case "image/png":
              return ["png"];
            case "image/gif":
              return ["gif"];
            case "image/webp":
              return ["webp"];
            case "image/svg+xml":
              return ["svg"];
            case "application/pdf":
              return ["pdf"];
            case "text/plain":
              return ["txt"];
            case "application/json":
              return ["json"];
            case "text/csv":
              return ["csv"];
            default:
              // If it's already an extension (not a MIME type), use it as-is
              if (!mimeType.includes("/")) {
                return [mimeType.toLowerCase()];
              }
              return [];
          }
        })
        .flat();

      if (extension && !allowedExtensions.includes(extension)) {
        return false;
      }
    }

    return true;
  });

  // Handle selection
  const handleSelectItem = (item: FileSystemItem, multiSelect?: boolean) => {
    if (item.isFolder) {
      navigateToPath(item.path);
      return;
    }

    if (mergedConfig.selectionMode === "single") {
      selectItem(item, false);
    } else {
      selectItem(item, multiSelect);
    }
  };

  // Handle double click
  const handleDoubleClick = (item: FileSystemItem) => {
    if (item.isFolder) {
      navigateToPath(item.path);
    }
  };

  // Handle file upload
  const handleFileUpload = async (files: File[]) => {
    try {
      await uploadMultipleFiles(files);
    } catch (err) {
      console.error("File upload error in handleFileUpload:", err);
      // Error is handled in the hook
    }
  };

  // Handle manual file selection via input
  const handleUploadClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    if (mergedConfig.allowedFileTypes) {
      input.accept = mergedConfig.allowedFileTypes.join(",");
    }
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        handleFileUpload(files);
      }
    };
    input.click();
  };

  // Handle folder creation
  const handleCreateFolder = async (folderName: string) => {
    await createFolder(folderName);
    setShowCreateFolderDialog(false);
  };

  const selectedFileCount = (selectedItems || []).filter(
    (item) => item && !item.isFolder,
  ).length;

  // Notify parent component when selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedItems || []);
    }
  }, [selectedItems, onSelectionChange]);

  return (
    <>
      {/* Drop zone wrapper for entire content area */}
      <div
        {...getRootProps()}
        className={`flex flex-col ${isDark ? "bg-auth-card" : "bg-white"} transition-all duration-300 ${
          isDragActive
            ? "bg-primary/5 border-2 border-dashed border-primary"
            : ""
        } ${className}`}
      >
        <input {...getInputProps()} />

        {/* Drag overlay */}
        {isDragActive && (
          <div className="absolute inset-0 bg-primary/10 backdrop-blur-sm flex items-center justify-center z-50 border-2 border-dashed border-primary m-6 rounded-lg">
            <div className="text-center">
              <Upload className="w-16 h-16 mx-auto text-primary mb-4" />
              <p className="text-xl font-medium text-primary">
                Drop files here to upload
              </p>
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col px-6 overflow-hidden">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-4">
            <div className="flex flex-wrap items-center gap-2">
              {/* Bulk Actions - Show when items are selected */}
              {(selectedItems || []).length > 0 && (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mr-2 ${isDark ? "bg-blue-900/30 border border-blue-500/50" : "bg-blue-50 border border-blue-200"}`}>
                  <span className={`text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-700"}`}>
                    {(selectedItems || []).length} selected
                  </span>
                  <ButtonDestructive
                    size="sm"
                    onClick={() => {
                      const filesToDelete = (selectedItems || []).filter(
                        (item) => !item.isFolder,
                      );
                      if (filesToDelete.length === 0) {
                        setError("Please select files to delete");
                        return;
                      }
                      if (
                        confirm(
                          `Are you sure you want to delete ${filesToDelete.length} file(s)?`,
                        )
                      ) {
                        filesToDelete.forEach((item) => deleteAsset(item));
                        clearSelection();
                      }
                    }}
                    className="transition-all duration-200"
                  >
                    Delete (
                    {
                      (selectedItems || []).filter((item) => !item.isFolder)
                        .length
                    }
                    )
                  </ButtonDestructive>
                  <ButtonOutline
                    size="sm"
                    onClick={clearSelection}
                    className="transition-all duration-200 hover:bg-primary hover:text-primary-foreground"
                  >
                    Clear
                  </ButtonOutline>
                </div>
              )}

              {/* Select All / Deselect All */}
              {filteredItems.length > 0 && (
                <ButtonOutline
                  size="sm"
                  onClick={() => {
                    const allSelected = filteredItems.every((item) =>
                      isItemSelected(item),
                    );
                    if (allSelected) {
                      clearSelection();
                    } else {
                      filteredItems.forEach((item) => {
                        if (!isItemSelected(item)) {
                          selectItem(item, true);
                        }
                      });
                    }
                  }}
                  className="h-10 px-3 flex items-center space-x-2 text-sm transition-colors"
                >
                  <Check className="w-4 h-4" />
                  <span>
                    {filteredItems.every((item) => isItemSelected(item))
                      ? "Deselect All"
                      : "Select All"}
                  </span>
                </ButtonOutline>
              )}

              <ButtonOutline
                size="sm"
                onClick={navigateUp}
                disabled={loading || currentPath === "/"}
                className="h-10 px-3 flex items-center space-x-2 text-sm transition-colors"
              >
                <ArrowUp className="w-4 h-4" />
                <span>Up</span>
              </ButtonOutline>
              <ButtonOutline
                size="sm"
                onClick={() => browseAssets()}
                disabled={loading}
                className="h-10 px-3 flex items-center space-x-2 text-sm transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Refresh</span>
              </ButtonOutline>
              {mergedConfig.showCreateFolder && (
                <ButtonOutline
                  size="sm"
                  onClick={() => setShowCreateFolderDialog(true)}
                  disabled={loading}
                  className="h-10 px-3 flex items-center space-x-2 text-sm transition-colors"
                >
                  <FolderPlus className="w-4 h-4" />
                  <span>New Folder</span>
                </ButtonOutline>
              )}
              {mergedConfig.showUpload && (
                <ButtonPrimary
                  size="sm"
                  onClick={handleUploadClick}
                  disabled={loading}
                  className="hover:shadow-lg hover:scale-105 transition-all duration-200 h-10 px-3 flex items-center space-x-2 text-sm"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload Files</span>
                </ButtonPrimary>
              )}
            </div>

            <div className="w-full sm:w-auto sm:flex-1 sm:max-w-sm">
              <Input
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`rounded-full transition-all duration-200 focus:ring-2 focus:ring-primary ${
                  isDark
                    ? "bg-auth-background border-auth-background text-white placeholder-gray-400"
                    : "bg-white border-gray-200"
                }`}
              />
            </div>
          </div>

          {/* Breadcrumb */}
          <Breadcrumb currentPath={currentPath} onNavigate={navigateToPath} />

          {/* Selection Info */}
          {selectedFileCount > 0 && (
            <div className="bg-primary/10 border-2 border-primary/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-primary font-medium">
                {selectedFileCount} file{selectedFileCount !== 1 ? "s" : ""}{" "}
                selected
                {mergedConfig.maxSelections && (
                  <span className="ml-2 text-primary/80">
                    ({selectedFileCount}/{mergedConfig.maxSelections})
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border-2 border-destructive/20 rounded-lg p-3 mb-4">
              <p className="text-sm text-destructive font-medium">{error}</p>
            </div>
          )}

          {/* Upload Progress */}
          {uploadProgress && Object.keys(uploadProgress).length > 0 && (
            <div
              className={`border-2 border-border rounded-lg p-4 mb-4 shadow-sm ${isDark ? "bg-auth-background border-auth-background" : "bg-white border-gray-200"}`}
            >
              <h4 className="text-sm font-medium text-foreground flex items-center space-x-2 mb-3">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                <span>Uploading Files</span>
              </h4>
              {Object.entries(uploadProgress).map(([filename, progress]) => (
                <div key={filename} className="space-y-2 mb-3 last:mb-0">
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground truncate font-medium">
                      {filename}
                    </span>
                    <span className="text-muted-foreground font-mono">
                      {progress}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* File Grid */}
          <ScrollArea
            className={`flex-1 border-2 rounded-lg shadow-sm mb-4 ${isDark ? "bg-auth-background border-auth-background" : "bg-white border-gray-200"}`}
          >
            <div className="p-6">
              {loading ? (
                <div className="text-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-muted-foreground text-lg">
                    {searchQuery
                      ? "No files match your search"
                      : "This folder is empty"}
                  </p>
                  {!searchQuery && mergedConfig.showUpload && (
                    <div className="mt-4">
                      <p className="text-muted-foreground/70 text-sm mb-4">
                        Upload files by clicking the button above or drag and
                        drop files anywhere in this area
                      </p>
                      <ButtonPrimary
                        size="sm"
                        onClick={handleUploadClick}
                        className="shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Your First File
                      </ButtonPrimary>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-3">
                  {/* Upload progress items */}
                  {(uploadProgressItems || []).map((progressItem) => (
                    <UploadProgressItem
                      key={progressItem.uploadId}
                      fileName={progressItem.fileName}
                      fileSize={progressItem.fileSize}
                      fileType={progressItem.fileType}
                      progress={progressItem.progress}
                      status={progressItem.status}
                      error={progressItem.error}
                      onCancel={() => cancelUpload(progressItem.uploadId)}
                    />
                  ))}

                  {/* Regular file items */}
                  {filteredItems.map((item) => (
                    <FileItem
                      key={item.path}
                      item={item}
                      isSelected={isItemSelected(item)}
                      selectionMode={mergedConfig.selectionMode || "multiple"}
                      showCheckbox={true}
                      onSelect={handleSelectItem}
                      onDoubleClick={handleDoubleClick}
                      onDelete={deleteAsset}
                      allowDelete={true}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      <CreateFolderDialog
        isOpen={showCreateFolderDialog}
        onClose={() => setShowCreateFolderDialog(false)}
        onCreateFolder={handleCreateFolder}
        loading={loading}
      />
    </>
  );
};
